import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clearCommitteeContext } from '@/utils/committeeContext';
import { Button } from '@/components/ui/button';
import { Award, ArrowRight, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  useEffect(() => {
    clearCommitteeContext();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/1k2v2h81_AHEP_CA_LOGO.jfif" 
                alt="AHEPA Logo" 
                className="w-10 h-10 object-contain rounded-full"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/gl3qcnxw_DOP_LOGO_LARGE.jfif" 
                alt="Daughters of Penelope Logo" 
                className="w-10 h-10 object-contain rounded-full"
              />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold" style={{ color: '#0D5EAF' }}>AHEPA Family</h1>
              <p className="text-xs text-slate-500">Scholarship Program</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="hover:bg-slate-100" style={{ color: '#0D5EAF' }} data-testid="nav-login-btn">
                Log In
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="text-white rounded-sm" style={{ background: '#0D5EAF' }} data-testid="nav-signup-btn">
                Apply Now
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative text-white py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0D5EAF 0%, #094078 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="font-heading text-5xl md:text-6xl font-bold leading-tight">
                AHEPA &amp; Daughters of Penelope Scholarship Program
              </h1>
              <p className="text-lg text-white/80 leading-relaxed">
                Apply now and take the next step in your academic journey with the support of the AHEPA Family Scholarships available in your area.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/signup">
                  <Button className="rounded-sm px-8 py-6 text-base font-semibold tracking-wide uppercase shadow-lg" style={{ background: '#D4AF37', color: 'white' }} data-testid="hero-apply-btn">
                    Start Your Application
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="rounded-sm px-8 py-6 text-base font-semibold tracking-wide uppercase" style={{ border: '2px solid white', color: 'white', background: 'transparent' }} data-testid="hero-login-btn">
                    Continue Application
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:flex flex-col items-center justify-center">
              <div className="flex items-center justify-center gap-8 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/1k2v2h81_AHEP_CA_LOGO.jfif" 
                  alt="Canadian Order of AHEPA Logo" 
                  className="w-36 h-36 object-contain rounded-full bg-white p-2 shadow-xl"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/gl3qcnxw_DOP_LOGO_LARGE.jfif" 
                  alt="Daughters of Penelope Logo" 
                  className="w-36 h-36 object-contain rounded-full bg-white p-2 shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
        {/* Gold border */}
        <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: '#D4AF37' }}></div>
      </section>

      {/* Eligibility Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl font-bold text-slate-900 mb-6">Eligibility Requirements</h2>
              <div className="space-y-4">
                  {[
                  "Open to students, including those who are not of Greek descent but who identify as Philhellenes",
                  "Currently attending or enrolling in an accredited college or university",
                  "Affiliation with AHEPA, Daughters of Penelope, or related organizations",
                  "Demonstrated academic achievement",
                  "Community involvement and leadership"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#D4AF37' }} />
                    <p className="text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-8 rounded-sm shadow-lg" style={{ borderLeft: '4px solid #D4AF37' }}>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">Application Checklist</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: '#D4AF37' }}></div>
                  Completed application form
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: '#D4AF37' }}></div>
                  Official transcripts
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: '#D4AF37' }}></div>
                  Proof of enrollment
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: '#D4AF37' }}></div>
                  Endorsement by a current AHEPA Family Member
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white" style={{ background: '#0D5EAF' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl font-bold mb-6">Ready to Apply?</h2>
          <p className="text-white/80 text-lg mb-8">
            Join the legacy of AHEPA scholars. Start your application today and take the next step in your academic journey.
          </p>
          <Link to="/signup">
            <Button className="rounded-sm px-10 py-6 text-lg font-semibold tracking-wide uppercase shadow-lg" style={{ background: '#D4AF37', color: 'white' }} data-testid="cta-apply-btn">
              Begin Application
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="greek-border mb-8"></div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/1k2v2h81_AHEP_CA_LOGO.jfif" 
                  alt="AHEPA Logo" 
                  className="w-12 h-12 object-contain rounded-full bg-white p-1"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/gl3qcnxw_DOP_LOGO_LARGE.jfif" 
                  alt="Daughters of Penelope Logo" 
                  className="w-12 h-12 object-contain rounded-full bg-white p-1"
                />
              </div>
              <div>
                <h3 className="font-heading font-bold">AHEPA &amp; Daughters of Penelope</h3>
                <p className="text-sm text-slate-400">Scholarship Program</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center mt-8 pt-8 border-t border-slate-800 text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} AHEPA &amp; Daughters of Penelope Scholarship Program. All rights reserved.</p>
            <div className="flex items-center gap-4 mt-2 md:mt-0">
              <Link to="/login" className="text-slate-400 hover:text-white font-medium" data-testid="footer-committee-link">
                Committee Member Login
              </Link>
              <span className="text-slate-600">|</span>
              <Link to="/admin/login" className="text-slate-400 hover:text-white font-medium" data-testid="footer-admin-link">
                Administrator
              </Link>
            </div>
          </div>
          {/* Spacer to prevent badge overlap */}
          <div className="h-12"></div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
